import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, ChevronDown, ChevronRight, Search, ExternalLink } from "lucide-react";

import CardTip from "@/components/CardTip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COMPLETED_TIP = [
  "The queue is the personal campaign built for the user that morning (6 am). Leads added later in the day aren't counted.",
  "A lead counts as completed if ANY of these happened that day: the user logged a disposition on it, the user dialed it at least once, or the due task that put it in the queue was completed or cancelled.",
  "Today only: a lead that is no longer in the user's live queue also counts as completed (shown as \"Left queue\", time unknown).",
  "Only this user's own dials and dispositions count. Activity by another rep doesn't.",
  "Remaining = leads in the queue minus completed.",
];
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");
const ROLE = { sales_agent: "Sales agent", call_agent: "Call agent", sales_manager: "Sales manager", demo_agent: "Demo agent", super_admin: "Super admin", crm_admin: "CRM admin", contracts_agent: "Contracts agent", bookings_agent: "Bookings agent", read_only: "Read only" };
const barColor = (r) => (r.total && r.remaining === 0 ? "#1D9E75" : r.completed > 0 ? "#2563eb" : "#c7c7cc");

function ProgressBar({ row }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: "#f0f0f2", borderRadius: 3, minWidth: 90 }}>
        <div style={{ width: `${row.pct}%`, height: 6, borderRadius: 3, background: barColor(row), transition: "width 300ms" }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, width: 34, textAlign: "right" }}>{row.pct}%</span>
    </div>
  );
}

function LeadList({ row }) {
  return (
    <div data-testid={`pc-leads-${row.user_id}`} style={{ padding: "4px 16px 12px 40px", background: "#fafafa" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead><tr style={{ color: "#888", textAlign: "left" }}>
          {["#", "Lead", "Why in queue", "Status", "Time", ""].map(h => <th key={h} style={{ padding: "6px 8px", fontWeight: 500 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {row.leads.map((l, i) => (
            <tr key={l.lead_id} data-testid={`pc-lead-${row.user_id}-${i}`} style={{ borderTop: "0.5px solid #eee" }}>
              <td style={{ padding: "6px 8px", color: "#aaa", width: 28 }}>{i + 1}</td>
              <td style={{ padding: "6px 8px", fontWeight: 500 }}>{l.name}{l.contact_name && l.contact_name !== l.name ? <span style={{ color: "#999", fontWeight: 400 }}> · {l.contact_name}</span> : null}</td>
              <td style={{ padding: "6px 8px", color: "#555" }}>{l.reason}</td>
              <td style={{ padding: "6px 8px" }}>
                {l.status === "completed"
                  ? <span style={{ color: "#1D9E75", fontWeight: 600 }}>Completed <span style={{ fontWeight: 400, color: "#555" }}>· {l.completed_via}</span></span>
                  : <span style={{ color: "#999" }}>Not started</span>}
              </td>
              <td style={{ padding: "6px 8px", color: "#555" }}>{l.completed_at ? fmtTime(l.completed_at) : l.status === "completed" ? "Time unknown" : "—"}</td>
              <td style={{ padding: "6px 8px", textAlign: "right" }}>
                <a href={`/lead-focus/${l.lead_id}`} target="_blank" rel="noreferrer" data-testid={`pc-lead-open-${row.user_id}-${i}`} style={{ color: "#185FA5", display: "inline-flex", alignItems: "center", gap: 3, textDecoration: "none" }}>Open <ExternalLink style={{ width: 10, height: 10 }} /></a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({ row, open, onToggle }) {
  const canOpen = row.has_queue && row.total > 0;
  return (
    <>
      <tr data-testid={`pc-row-${row.user_id}`} onClick={canOpen ? onToggle : undefined} style={{ borderTop: "0.5px solid #eee", cursor: canOpen ? "pointer" : "default" }}>
        <td style={{ padding: "10px 8px", width: 24 }}>{canOpen && (open ? <ChevronDown style={{ width: 13, height: 13 }} /> : <ChevronRight style={{ width: 13, height: 13 }} />)}</td>
        <td style={{ padding: "10px 8px" }}><div style={{ fontSize: 12, fontWeight: 600 }}>{row.name}</div><div style={{ fontSize: 10, color: "#888" }}>{ROLE[row.role] || row.role}{row.source === "preview" ? " · preview (no morning build yet)" : ""}</div></td>
        {canOpen ? (
          <>
            <td style={{ padding: "10px 8px", fontSize: 12 }}>{row.total}</td>
            <td style={{ padding: "10px 8px", fontSize: 12 }}><span style={{ color: "#1D9E75", fontWeight: 600 }}>{row.completed}</span> / <span>{row.remaining}</span></td>
            <td style={{ padding: "10px 8px", width: 220 }}><ProgressBar row={row} /></td>
            <td style={{ padding: "10px 8px", fontSize: 11, color: "#555" }}>{row.last_completed_at ? fmtTime(row.last_completed_at) : row.completed > 0 ? "Time unknown" : "—"}</td>
          </>
        ) : (
          <td colSpan={4} data-testid={`pc-noqueue-${row.user_id}`} style={{ padding: "10px 8px", fontSize: 11, color: "#999" }}>{row.error ? "Couldn't load this user's queue" : "No personal queue that day"}</td>
        )}
      </tr>
      {open && canOpen && <tr><td colSpan={6} style={{ padding: 0 }}><LeadList row={row} /></td></tr>}
    </>
  );
}

function Shimmer() {
  return <div data-testid="pc-loading">{[0, 1, 2, 3, 4].map(i => <div key={i} className="ehi-shimmer" style={{ height: 34, borderRadius: 6, marginBottom: 8 }} />)}</div>;
}

const SORTS = {
  pct: (a, b) => b.pct - a.pct || a.name.localeCompare(b.name),
  remaining: (a, b) => b.remaining - a.remaining || a.name.localeCompare(b.name),
  name: (a, b) => (a.name || "").localeCompare(b.name || ""),
};

export default function PersonalCampaignsPanel({ token }) {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("pct");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/personal-campaign-progress`, { headers: { Authorization: `Bearer ${token}` }, params: date ? { date } : {} });
      setData(r.data); setError(""); setUpdatedAt(new Date());
    } catch (e) { setError(e.response?.data?.detail || "Couldn't load personal campaigns"); }
    finally { setLoading(false); }
  }, [token, date]);

  useEffect(() => { load(false); const t = setInterval(() => load(true), 60000); return () => clearInterval(t); }, [load]);

  const rows = useMemo(() => {
    const list = (data?.users || []).filter(r => !q.trim() || (r.name || "").toLowerCase().includes(q.trim().toLowerCase()));
    const withQ = list.filter(r => r.has_queue && r.total > 0).sort(SORTS[sort]);
    const without = list.filter(r => !(r.has_queue && r.total > 0)).sort(SORTS.name);
    return [...withQ, ...without];
  }, [data, q, sort]);
  const s = data?.summary;

  return (
    <div data-testid="personal-campaigns-panel" className="card" style={{ padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <div className="card-title" style={{ margin: 0 }}>Personal campaigns</div>
        <input type="date" data-testid="pc-date" value={date} onChange={e => { setDate(e.target.value); setOpenId(null); }} style={{ fontSize: 11, padding: "5px 8px", border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }} />
        {date && <button data-testid="pc-today" onClick={() => setDate("")} style={{ fontSize: 11, border: "none", background: "none", color: "#185FA5", cursor: "pointer", fontFamily: "var(--font)" }}>Today</button>}
        <div style={{ position: "relative" }}>
          <Search style={{ position: "absolute", left: 8, top: 7, width: 12, height: 12, color: "#999" }} />
          <input data-testid="pc-search" placeholder="Search user" value={q} onChange={e => setQ(e.target.value)} style={{ fontSize: 11, padding: "5px 8px 5px 26px", border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)", width: 160 }} />
        </div>
        <select data-testid="pc-sort" value={sort} onChange={e => setSort(e.target.value)} style={{ fontSize: 11, padding: "5px 8px", border: "0.5px solid var(--border)", borderRadius: 6, fontFamily: "var(--font)" }}>
          <option value="pct">Sort: % complete</option><option value="remaining">Sort: remaining</option><option value="name">Sort: name</option>
        </select>
        <div style={{ flex: 1 }} />
        {updatedAt && <span data-testid="pc-updated" style={{ fontSize: 10, color: "#999" }}>Updated {updatedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>}
        <button data-testid="pc-refresh" onClick={() => load(false)} title="Refresh" style={{ border: "0.5px solid var(--border)", background: "#fff", borderRadius: 6, padding: "4px 8px", cursor: "pointer", display: "inline-flex" }}><RefreshCw style={{ width: 12, height: 12 }} className={loading ? "animate-spin" : ""} /></button>
      </div>
      {s && !loading && (
        <div data-testid="pc-summary" style={{ fontSize: 12, color: "#333", marginBottom: 12, padding: "8px 12px", background: "#f7f9fc", borderRadius: 8 }}>
          Team: <b>{s.completed}</b> of <b>{s.total}</b> leads completed (<b>{s.pct}%</b>) · <b>{s.users_finished}</b> of {s.users_with_queue} users finished their queue
        </div>
      )}
      {error && <div data-testid="pc-error" style={{ fontSize: 11, color: "#E24B4A", marginBottom: 8 }}>{error}</div>}
      {loading ? <Shimmer /> : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ fontSize: 10, color: "#888", textAlign: "left", textTransform: "uppercase", letterSpacing: 0.3 }}>
            {["", "User", "In queue", "Completed / remaining", "Progress", "Last completed"].map(h => (
              <th key={h} style={{ padding: "6px 8px", fontWeight: 500 }}>
                {h === "Completed / remaining" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{h}<span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, color: "var(--text-primary, #111)" }}><CardTip testId="pc-completed-tip" align="left" title="How completions are counted" items={COMPLETED_TIP} /></span></span> : h}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map(r => <UserRow key={r.user_id} row={r} open={openId === r.user_id} onToggle={() => setOpenId(openId === r.user_id ? null : r.user_id)} />)}
            {rows.length === 0 && <tr><td colSpan={6} data-testid="pc-empty" style={{ padding: 24, textAlign: "center", fontSize: 11, color: "#888" }}>No dialer users match.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
